import { isAxiosError } from "axios";
import { clsx, type ClassValue } from "clsx";
import format from "date-fns/format";
import parse from "date-fns/parse";
import { twMerge } from "tailwind-merge";

import { FeedVersion } from "@/lib/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function atUriToBskyUrl(atUri: string) {
  // https://bsky.app/starter-pack/propublica.org/3l6iflmcj322n
  let url = atUri
    .replace("at://", "https://bsky.app/profile/")
    .replace("app.bsky.feed.list", "list")
    .replace("app.bsky.feed.post", "post")
    .replace("app.bsky.feed.generator", "feed");

  if (url.includes("app.bsky.graph.starterpack")) {
    url = url.replace("/profile/", "/starter-pack/");
    url = url.replace("app.bsky.graph.starterpack/", "");
  }

  return url;
}

/**
 * The same list URL, but naming its owner by DID rather than by handle.
 *
 * 🔴 HANDLES ROT AND DIDS DO NOT. A stored `bsky.app/profile/<handle>/lists/<rkey>`
 * stops being resolvable the moment its owner renames, and nothing tells anyone:
 * the asset keeps serving from cache while it silently stops refreshing. Measured
 * 2026-09-24 across live conditions: 786 of 5,331 stored actor references were
 * handles, and **100 of those handles no longer resolved**.
 *
 * Lists chosen through the picker were always fine — it builds the URL from the
 * API's AT-URI, which carries a DID. The rot comes from a creator pasting the URL
 * out of their browser's address bar, which is the handle form.
 *
 * `canonicalUri` is the list's own `uri` as the asset API returned it, so this
 * costs no extra lookup: by the time a preview has rendered, the DID is already in
 * hand.
 *
 * Returns the input unchanged when there is nothing to do or anything looks off —
 * a different rkey, an unparseable URL, a canonical URI that is not a DID. This
 * runs behind a creator's back, so it must never turn a URL they can read into one
 * they cannot, and never point a rule at a different list than the one they chose.
 */
export function listUrlByDid(storedUrl: string, canonicalUri: string) {
  const stored = storedUrl.match(
    /^https:\/\/bsky\.app\/profile\/([^/]+)\/lists\/([^/?#]+)/,
  );
  if (!stored) return storedUrl;

  const [, actor, rkey] = stored;
  if (!actor || !rkey || actor.startsWith("did:")) return storedUrl;

  const canonical = canonicalUri.match(
    /^at:\/\/(did:[^/]+)\/app\.bsky\.graph\.list\/([^/?#]+)$/,
  );
  if (!canonical) return storedUrl;

  const [, did, canonicalRkey] = canonical;
  // Same list, or we keep our hands off it.
  if (!did || canonicalRkey !== rkey) return storedUrl;

  return `https://bsky.app/profile/${did}/lists/${rkey}`;
}

export function urlToAtUri(url: string) {
  // https://bsky.app/starter-pack/propublica.org/3l6iflmcj322n
  let atUri = url
    .replace("https://bsky.app/profile/", "at://")
    .replace("/list/", "/app.bsky.feed.list/")
    .replace("/post/", "/app.bsky.feed.post/")
    .replace("/feed/", "/app.bsky.feed.generator/");

  if (atUri.includes("starter-pack")) {
    atUri = atUri.replace("/starter-pack/", "app.bsky.graph.starterpack/");
    atUri = atUri.replace("at://", "https://bsky.app/profile/");
  }

  return atUri;
}

export function isVideoFeed(feedVersion: FeedVersion | undefined) {
  return Boolean(
    feedVersion?.manifest.filter &&
      "and" in feedVersion.manifest.filter &&
      feedVersion?.manifest.filter["and"]?.some(
        (child) =>
          "embed_type" in child &&
          child.embed_type?.[0] === "==" &&
          child.embed_type?.[1] === "video",
      ),
  );
}

export function formatDate(date: string) {
  return format(parse(date, "yyyy-MM-dd", new Date()), "MM/dd");
}

export function formatBigNumber(number: number) {
  return Intl.NumberFormat("en-us", {
    notation: "compact",
    maximumSignificantDigits: 3,
  }).format(number);
}

/**
 * Human-readable text for a failed API call.
 *
 * Axios sets `error.message` to "Request failed with status code 400", which tells a user
 * nothing. Our FastAPI backends put the useful part in the response body: `detail` is a string
 * for an explicit HTTPException (e.g. "Unsupported post URI format: ...") and an array of
 * `{msg}` objects for a request-validation failure. Prefer either over the generic message.
 */
export function apiErrorMessage(error: unknown): string {
  const detail = isAxiosError(error)
    ? (error.response?.data as { detail?: unknown } | undefined)?.detail
    : undefined;

  if (typeof detail === "string" && detail.trim()) return detail;

  if (Array.isArray(detail)) {
    const messages = detail
      .map((d) => (typeof d === "string" ? d : (d as { msg?: unknown })?.msg))
      .filter((m): m is string => typeof m === "string" && m.trim().length > 0);
    if (messages.length) return messages.join("; ");
  }

  if (error instanceof Error && error.message) return error.message;
  return String(error);
}
